/**
 * client/src/lib/logout.ts — Shared logout handler
 *
 * Security invariant (CLAUDE.md §Session Policy):
 *   POST /api/auth/logout → window.location.href = "/auth"
 *   Server-side handles: DELETE session + close WebSockets.
 *
 * The vault KEK and the PRF output are NOT among them. Both are CLIENT-derived
 * (L0-6 / L0-9) and never serialised into the session row, so the server holds
 * no buffer to atomicWipe — an earlier version of this header claimed it did,
 * which read as assurance the server cannot actually provide (Friction Rule
 * clause 2). The KEK slot here is dropped by the full-page navigation below,
 * and by `clearActiveKek()` on the session-death 401 path in queryClient.ts
 * (AC-48) for sessions killed server-side without a logout.
 */
import { postJSON } from "@/lib/queryClient";
import { forgetChosenCredential } from "@/lib/webauthn/chosen-credential";
import { clearConfirmCardDismissed } from "@/lib/memory-confirm-card-storage";

export async function handleLogout(): Promise<void> {
  // "Later" on the day-2 confirm card lasts until the next sign-in (spec 03,
  // AC-21); this sign-out ends the login it belonged to.
  clearConfirmCardDismissed();
  // Which passkey THIS person chose is not a fact about the next person to sign
  // in on this machine (@/lib/webauthn/chosen-credential). Using a stale one
  // is already harmless — it is only ever applied when the server still offers
  // that id — but a shared laptop should not carry the preference across.
  forgetChosenCredential();
  try {
    await postJSON("/api/auth/logout", {});
  } catch {
    /* session may already be gone */
  }
  window.location.href = "/auth";
}
